import { IApiClient } from '@api/core/api.client';
import { ApiError } from '@api/core/api.error';
import env from '@config/env';
import * as Sentry from '@sentry/react';
import axios, { AxiosError } from 'axios';
import qs from 'qs';

const client = axios.create({
  baseURL: env.API_URL,
  withCredentials: true,
  paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'brackets' }),
});

client.interceptors.response.use(
  (res) => res.data,
  (error: AxiosError) => {
    const apiError = new ApiError(error.response?.status, error.message, error.response?.data);

    Sentry.withScope((scope) => {
      scope.setTag('transport', 'axios');
      scope.setTag('http.status_code', String(error.response?.status || 'unknown'));
      if (error.config?.method) {
        scope.setTag('http.method', error.config.method.toUpperCase());
      }
      if (error.config?.url) {
        scope.setExtra('http.url', error.config.url);
      }
      if (error.response?.data) {
        scope.setContext('response', {
          data: error.response.data,
        });
      }
      Sentry.captureException(apiError);
    });

    return Promise.reject(apiError);
  },
);

export class AxiosClient implements IApiClient {
  public get<R>(url: string, query: Record<string, any>): Promise<R> {
    return client.get(url, {
      params: query,
    });
  }

  public post<P, R>(url: string, payload: P): Promise<R> {
    return client.post(url, payload);
  }

  public put<P, R>(url: string, payload: P): Promise<R> {
    return client.put(url, payload);
  }

  public patch<P, R>(url: string, payload: P): Promise<R> {
    return client.patch(url, payload);
  }

  public async delete<P, R>(url: string, payload?: P): Promise<R> {
    return client.delete(url, {
      data: payload,
    });
  }
}
